import { BaseApiRequest, BaseApiResponse } from '@/types/common/api'
import { addToast } from '@heroui/react'
import { redirectToAuthLogin } from '@/utils/auth'

// API Base URL
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE

// Keep large id-like numbers as strings to avoid JS precision loss (e.g. Snowflake IDs).
const parseApiJsonSafely = (rawText: string): any => {
	if (!rawText) return {}

	// Quote 16+ digit integers for id/id-like keys before JSON.parse.
	// Examples: "id": 2044959817830313986, "jobId": 1234567890123456
	const normalized = rawText.replace(
		/("[A-Za-z0-9_]*(?:id|Id|ID)"\s*:\s*)(-?\d{16,})(?=\s*[,}\]])/g,
		'$1"$2"'
	)

	return JSON.parse(normalized)
}

// Toast duplication prevention state management
let activeToasts: Set<string> = new Set()

/**
 * Add Toast with duplication prevention
 * @param options Toast configuration options
 * @param uniqueKey Unique identifier for the toast, used for duplication check
 */
const addToastWithDeduplication = (options: any, uniqueKey: string): void => {
	// If a toast with the same key is already showing, don't add it again
	if (activeToasts.has(uniqueKey)) {
		return
	}

	// Add to active toasts collection
	activeToasts.add(uniqueKey)

	// Add Toast
	addToast({
		...options,
		// Use onClose callback to remove from collection when toast is closed
		onClose: () => {
			activeToasts.delete(uniqueKey)
			// Execute original onClose callback if it exists
			if (options.onClose) {
				options.onClose()
			}
		},
	})
}

/**
 * Handle unauthorized error (401)
 */
const handleUnauthorizedError = (): void => {
	// Ensure execution only in client environment
	if (typeof window !== 'undefined') {
		// Use deduplicated Toast with fixed key to ensure only one login expired message shows at a time
		addToastWithDeduplication(
			{
				color: 'danger',
				title: '会话过期',
				description: '您的会话已过期。请重新登录。',
				timeout: 3000,
			},
			'login-expired'
		)

		// Delay redirect to give user time to see the message
		setTimeout(() => {
			// Dynamically import user store and call logout method
			import('@/store/user').then(({ useUserStore }) => {
				const userStore = useUserStore.getState()
				userStore.logout()
				// Redirect to login page
				redirectToAuthLogin()
			})
		}, 3000) // 1 second delay to allow user to see the message
	}
}

/**
 * Generic fetch request function
 */
export const fetchClient = async <T>(options: BaseApiRequest): Promise<BaseApiResponse<T>> => {
	const {
		path,
		method,
		body,
		requireAuth = true,
		headers = {},
		...restOptions
	} = options

	const url = `${API_BASE_URL}${path}`

	// Build request headers
	const requestHeaders: HeadersInit = {
		'Content-Type': 'application/json',
		...headers
	}

	// Add authorization header
	if (requireAuth && typeof window !== 'undefined') {
		const token = localStorage.getItem('token')
		if (token) {
			requestHeaders.Authorization = `Bearer ${token}`
		}
	}

	try {
		const response = await fetch(url, {
			method,
			headers: requestHeaders,
			body: body ? JSON.stringify(body) : undefined,
			...restOptions
		})

		// Handle 401 unauthorized error
		if (response.status === 401) {
			handleUnauthorizedError()
			return {
				code: 401,
				status: 'error',
				msg: '未经授权的访问。请重新登录。',
				error: '未经授权的访问。请重新登录。'
			}
		}

		if (!response.ok) {
			throw new Error(`Request failed: ${response.status} ${response.statusText}`)
		}

		const rawText = await response.text()
		const data = parseApiJsonSafely(rawText)

		// Check success and code fields in response body
		if (data.success === false && data.code === 401) {
			handleUnauthorizedError()
			return {
				code: 401,
				status: 'error',
				msg: '会话已过期。请重新登录。',
				error: '会话已过期。请重新登录。'
			}
		}

		return data
	} catch (error) {
		console.error(`Request error (${url}):`, error)

		return {
			code: 500,
			status: 'error',
			msg: '请求失败',
			error: error instanceof Error ? error.message : '未知错误'
		}
	}
}

/**
 * 下载文件
 * @param path API路径
 * @param defaultFileName 默认文件名
 * @returns Promise<boolean> 是否成功下载
 */
export const downloadFile = async (path: string, defaultFileName: string = 'download.xlsx', params?: Record<string, any>): Promise<boolean> => {

	try {
		const url = `${API_BASE_URL}${path}`
		// 获取认证token
		const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
		// 构建请求头
		const headers: HeadersInit = {
			'Content-Type': 'application/json'
		}
		if (token) {
			headers.Authorization = `Bearer ${token}`
		}

		// 构建请求配置
		const requestConfig: RequestInit = {
			method: 'POST',
			headers,
			credentials: 'include'
		}
		// 如果有参数，添加到请求体中
		if (params) {
			requestConfig.body = JSON.stringify(params)
		}
		// 发起POST请求获取文件流
		const response = await fetch(url, requestConfig)


		if (!response.ok) {
			throw new Error(`下载文件失败: ${response.statusText}`)
		}

		// 获取文件名(从响应头中获取)
		const contentDisposition = response.headers.get('content-disposition')
		let fileName = defaultFileName

		if (contentDisposition) {
			const matches = /filename="?([^";]+)"?/.exec(contentDisposition);

			console.log('contentDisposition', matches)
			if (matches && matches[1]) {
				fileName = matches[1]
			}
		}

		// 将响应转换为Blob
		const blob = await response.blob()

		// 创建下载链接
		const downloadUrl = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = downloadUrl
		link.download = fileName

		// 触发下载
		document.body.appendChild(link)
		link.click()

		// 清理
		setTimeout(() => {
			document.body.removeChild(link)
			window.URL.revokeObjectURL(downloadUrl)
		}, 100)

		return true
	} catch (error) {
		console.error('下载文件失败:', error)
		return false
	}
}

/**
 * 上传
 * @param path API路径
 * @param file 要上传的文件
 * @returns Promise<BaseApiResponse> 上传结果
 */
export const uploadFile = async (path: string, file: File): Promise<BaseApiResponse> => {
	try {
		const url = `${API_BASE_URL}${path}`

		// 获取认证token
		const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null

		// 构建FormData
		const formData = new FormData()
		formData.append('file', file)
		// formData.append('updateSupport', 'true') // 是否支持更新已有数据

		// 构建请求头(不设置Content-Type,让浏览器自动设置multipart/form-data)
		const headers: HeadersInit = {}
		if (token) {
			headers.Authorization = `Bearer ${token}`
		}

		// 发起POST请求上传文件
		const response = await fetch(url, {
			method: 'POST',
			headers,
			body: formData,
			credentials: 'include'
		})

		if (!response.ok) {
			throw new Error(`上传文件失败: ${response.statusText}`)
		}

		// 解析响应
		const rawText = await response.text()
		const result = parseApiJsonSafely(rawText)
		return result as BaseApiResponse
	} catch (error) {
		console.error('上传文件失败:', error)
		return {
			code: 500,
			status: 'error',
			msg: '上传文件失败',
			error: error instanceof Error ? error.message : 'Unknown error'
		}
	}
}
